/**
 * Network-aware failure messages for server calls (BUG-2614).
 *
 * The service functions (orderServices.js and the rest) catch their own errors and
 * return undefined, so the screen that called them cannot tell a weak network from a
 * server fault and showed "Something is wrong in <x> service" for both. GPR's store
 * read that message on a very poor network on 2026-10-07 and could not tell it was
 * their internet.
 *
 * applyHealthInterceptors (healthReporter.js) runs on every axios instance, so it
 * records the outcome of every GraphQL call here, keyed by the operation's root field
 * (getEstimates, upsertCart, ...). The screen then asks serviceFailureMessage for the
 * text: a recent network / server failure of that operation gives a plain message the
 * cashier can act on; anything else keeps the screen's original text.
 */
import messages from "../errors/messages.en-IN.json";

// How long a recorded failure explains a message. A screen shows its message right
// after the call returns, so this only needs to cover that gap.
const FAILURE_FRESH_MS = 15 * 1000;

const _failures = new Map();

/** Root field of a GraphQL request body ("getEstimates" for `query GetEstimates(...) { getEstimates(...) }`). */
export function getGraphqlOperation(data) {
  let body = data;
  if (typeof body === "string") {
    try {
      body = JSON.parse(body);
    } catch {
      return null;
    }
  }
  const query = body?.query;
  if (typeof query !== "string") return null;
  const brace = query.indexOf("{");
  if (brace < 0) return null;
  const match = /^\s*([A-Za-z_][A-Za-z0-9_]*)/.exec(query.slice(brace + 1));
  return match ? match[1] : null;
}

/**
 * Kind of failure, from an axios error:
 * "offline" | "slow" | "unreachable" | "server_error" | "session_expired" | null.
 * null = nothing the network explains (cancelled, a 4xx the screen handles itself).
 */
export function classifyRequestError(error, isOnline = typeof navigator === "undefined" ? true : navigator.onLine !== false) {
  if (!error) return null;
  const code = error.code;
  if (code === "ERR_CANCELED" || error.name === "CanceledError") return null;
  if (!isOnline) return "offline";
  const status = error.response?.status;
  if (status) {
    if (status >= 500) return "server_error";
    if (status === 401 || status === 403) return "session_expired";
    return null;
  }
  if (code === "ECONNABORTED" || code === "ETIMEDOUT" || /timeout/i.test(error.message || "")) return "slow";
  return "unreachable";
}

/** Called by applyHealthInterceptors for every response (error = undefined) and every failure. */
export function recordRequestOutcome(config, error) {
  const op = getGraphqlOperation(config?.data);
  if (!op) return;
  if (!error) {
    _failures.delete(op);
    return;
  }
  const kind = classifyRequestError(error);
  if (kind) _failures.set(op, { kind, at: Date.now() });
  else _failures.delete(op);
}

/** The recorded failure kind of an operation if it is recent, else null. */
export function getRecentFailure(op, now = Date.now()) {
  const entry = _failures.get(op);
  if (!entry) return null;
  if (now - entry.at > FAILURE_FRESH_MS) {
    _failures.delete(op);
    return null;
  }
  return entry.kind;
}

const KIND_MESSAGE_KEY = {
  offline: "offline",
  slow: "slow",
  unreachable: "unreachable",
  server_error: "server_error",
  session_expired: "session_expired",
};

/**
 * Text to show when a call of `op` did not give the screen what it needed.
 * Offline, or a recent network / server failure of that call: a message saying so.
 * Otherwise `fallback` (the screen's own text, unchanged).
 */
export function serviceFailureMessage(op, fallback) {
  const offline = typeof navigator !== "undefined" && navigator.onLine === false;
  const kind = offline ? "offline" : getRecentFailure(op);
  const text = kind ? messages.errors?.network?.[KIND_MESSAGE_KEY[kind]] : null;
  return text || fallback;
}

/** Test helper. */
export function _resetRequestFailures() {
  _failures.clear();
}
